import { BranchStatus } from "./constants/branch-status";
import { TipStatusPaymentEnum, TransactionStatusEnum } from "./enums/payment";
import { DayScheduleDto } from "./services/dto/response/schedule-response-dto";

export type Branch = {
  branchId: number;
  companyId: number;
  name: string;
  address: string;
  phone: string;
  active: boolean;
  status: BranchStatus;
  employeeCount: number;
  equalSplitTip: boolean;
  onlyRatings: boolean;
  googleLink: string | null;
  alias: string | null;
  withTipAccount: boolean;
  fantasyName?: string | null;
};

export type PaycloudBranch = Branch & {
  tributaryIdentifierType: string;
  tributaryIdentifier: string;
  floor: string;
  department: string;
  postalCode: string;
  cityId: number;
  departmentId: number;
  activityAddress: string;
  activityPostalCode: string;
  activityCityId: number;
  activityDepartmentId: number;
  activityDepartment: string;
  activityFloor: string;
  isPhysicalPerson: boolean;
  taxPayer: boolean;
  bankAccountNumber: string;
  identifier: string;
  paycloudEntityId: number;
  paycloudUserId: number;
  managerName: string;
  managerLastName: string;
  managerEmail: string;
  managerPhone: string;
  managerDocumentType: string;
  managerDocumentNumber: string;
  managerAddress: string;
  managerCityId: number;
  managerProvinceId: number;
  cloverExternalId?: string;
  wasAuthorized?: boolean;
  managerDniFront?: string | null;
  managerDniBack?: string | null;
  managerSelfie?: string | null;
  afipFile?: string | null;
  origins: string[];
  manualTipsProccess?: boolean;
  considerPaymentEmployee?: boolean;
};

export type Group = {
  groupId: number;
  branchId: number;
  name: string;
  percent: string;
  active: boolean;
  createdDate: string;
  updatedDate: string;
  employeeCount: number;
};

export type BranchFiles = {
  files: {
    managerDniFront: string | null;
    managerDniBack: string | null;
    managerSelfie: string | null;
    afipFile: string | null;
  };
};

export type Employee = {
  userId: number;
  roleId: number;
  companyId: number;
  name: string;
  email: string;
  password: string;
  isVerified: boolean;
  address: string | null;
  phone: string | null;
  birthdate: string | null;
  dni: string | null;
  identityNumber: string | null;
  bankAccountNumber: string | null;
  isFirstLogin: boolean;
  active: boolean;
  createdDate: string;
  updatedDate: string | null;
};

type UserGroups = {
  id: number;
  name: string;
  typeId: number;
  typeName: string;
};

type WorkScheduleBlock = {
  startTime: string;
  endTime: string;
};

type WorkScheduleDay = {
  dayOfWeek: number;
  blocks: WorkScheduleBlock[];
};

type WorkSchedule = {
  workScheduleId: number;
  name: string;
  days: WorkScheduleDay[];
};

export type EmployeeUser = {
  userId: number;
  name: string;
  branchId: number;
  branchName: string;
  fantasyName?: string | null;
  branchOrigin: string;
  isVerified: boolean;
  useQR: boolean;
  email: string;
  imageUrl: string | null;
  equalSplitTip: boolean;
  onLeave: boolean;
  userGroups: UserGroups[];
  groupsTypes: UserGroups[];
  workSchedules?: WorkSchedule[];
  phone: string | null;
  address: string | null;
  birthDate: string | null;
  dni: string | null;
  identityNumber: string | null;
  bankAccountNumber: string | null;
  qrTipsCode: string | null;
  onlyRatings: boolean;
  googleLink?: string | null;
  qrDisplayConfigId?: number | null;
  qrType?: string | null;
  qrDisplayShortName?: string | null;
  qrDisplayDescription?: string | null;
};

export type User = {
  userId: number;
  name: string;
  email: string;
  rol: string;
  createdDate: string | null;
  isResponsible: boolean;
};

export type TokenPayload = {
  CompanyId: number;
  Email: string;
  Exp: number;
  RoleId: number;
  UserId: number;
  isFirstLogin: boolean;
  Portal: string;
  CountryCode: string;
};

type GroupTypes = {
  groupTypeId: number;
  code: string;
  name: string;
};

export const groupTypes: GroupTypes[] = [
  { groupTypeId: 1, code: "NORMAL", name: "Grupo" },
  { groupTypeId: 2, code: "QR_SPLIT", name: "Con QR" },
];

export type Rating = {
  ratingId: number;
  usersName: string;
  message: string;
  rating: number;
  branchName: string;
  fantasyName?: string | null;
  createdDate: string;
  isRead: boolean;
};

export interface Settings {
  name: string;
  identityNumber: string | null;
  address: string | null;
  phone: string | null;
  responsible: string | null;
  email: string | null;
  userId: number;
  userEmail: string;
}

export type TimeUnit = "hours" | "day" | "month";
export type CalculationType = "average" | "total";
export interface DashboardFilterOption {
  timeUnit: TimeUnit;
  calculationType: CalculationType;
}

export type Chart = {
  time: string;
  propinas: number;
  valoraciones: number;
};

export type Manager = {
  userId: number;
  name: string;
  email: string;
  active: boolean;
  branches: Branch[];
}

export type ManagerPaycloud = Manager & {
  firstName: string;
  lastName: string;
  identityNumber: string;
  address: string;
  phone: string;
  departmentId: number;
  cityId: number;
  isVerified: boolean;
}

export type Schedule = {
  name: string;
  schedulesDays: DayScheduleDto[];
  branchId: number;
  scheduleId: number;
};

export type PaymentTransaction = {
  id: number;
  date: string;
  amount: number;
  taxRetentionAmount?: number | null;
  transactionNetAmount?: number | null;
  employee: string;
  observation: string;
  status: TransactionStatusEnum;
  attempts: number;
  rejectedDate: string | null;
  accreditatedDate: string | null;
};

export type TipEarningsIssue = {
  status: "active" | "resolved";
  message: string;
  emailSentCount: number;
  lastEmailSentAt: string;
  errorCreatedAt: string;
  resolvedAt: string | null;
  manualTipsProccess?: boolean;
};

export type Payment = {
  id: number;
  date: string;
  tipAmount: number;
  branch: string;
  fantasyName?: string | null;
  employee: string;
  origin: string | null;
  status: TipStatusPaymentEnum;
  earningsIssue?: TipEarningsIssue | null;
  transactions: PaymentTransaction[];
};

export type PaymentsResponse = {
  payments: Payment[];
  totalItems: number;
  currentPage: number;
  totalPages: number;
  filters: PaymentFilters;
  totalTransferred?: number;
};

export type PaymentFilters = {
  branches: {
    id: number;
    name: string;
    fantasyName?: string | null;
  }[];
  employees: {
    id: number;
    name: string;
  }[];
  tipStatus: {
    id: number;
    name: string;
  }[];
  transactionsStatus: {
    id: number;
    name: string;
  }[];
  providers: {
    id: number;
    name: string;
  }[];
}

export interface BranchStyles {
  headerLandingColor: string;
  textLandingColor: string;
  logoImageId: string | null;
  photoLogo?: string;
  branchId: number;
}
